import { CommandProps, Editor } from '@tiptap/core';
import { Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import {
  AskAiSelectionHighlightAttributes,
  AskAiSelectionHighlightDecorationName,
  AskAiSelectionHighlightStorage,
} from './types';

const getAskAiHighlightStyleAttribute = (
  attributes?: AskAiSelectionHighlightAttributes,
  editor?: Editor,
) => {
  const getBackgroundColor = () => {
    // Check if editor is in inspect mode
    const editorElement = editor?.options.element as HTMLElement;
    const isInspectMode = editorElement?.classList.contains(
      'ask-ai-inspect-mode',
    );

    if (isInspectMode) {
      return 'transparent';
    }

    if (attributes?.actionType === 'enhance-with-notes') {
      return 'var(--purple-20)';
    }

    return 'var(--browser-selection-background)';
  };

  return {
    style: `
      line-height: inherit;
      color: inherit;
      background-color: ${getBackgroundColor()};
      padding-left: 0;
      padding-right: 0;
    `,
  };
};

export const decorationStorageManager = (editor: Editor, tr: Transaction) => {
  const extensionStorage = editor.storage[
    AskAiSelectionHighlightDecorationName
  ] as AskAiSelectionHighlightStorage;

  const getDecorationSet = (): Decoration[] => {
    const set = extensionStorage.decorationSet.get(editor);

    if (set == null) {
      return [];
    }

    return set.find();
  };
  const getRangeSet = () => {
    return extensionStorage.rangeSet.get(editor) ?? [];
  };
  const addRange = (from: number, to: number) => {
    const existingRanges = getRangeSet();

    extensionStorage.rangeSet.set(editor, [...existingRanges, { from, to }]);
  };
  const removeRange = (from: number, to: number) => {
    const existingRanges = getRangeSet();

    extensionStorage.rangeSet.set(
      editor,
      existingRanges.filter((range) => range.from !== from || range.to !== to),
    );
  };

  return {
    getDecorationSet,
    addDecoration: (
      from: number,
      to: number,
      attributes?: AskAiSelectionHighlightAttributes,
    ) => {
      addRange(from, to);
      extensionStorage.decorationSet.set(
        editor,
        DecorationSet.create(tr.doc, [
          ...getDecorationSet(),
          Decoration.inline(from, to, {
            nodeName: 'mark',
            ...getAskAiHighlightStyleAttribute(attributes, editor),
          }),
        ]),
      );
    },
    removeDecoration: (from: number, to: number) => {
      const set = extensionStorage.decorationSet.get(editor);

      if (set == null) {
        return;
      }
      removeRange(from, to);

      extensionStorage.decorationSet.set(
        editor,
        set.remove(set.find(from, to)),
      );
    },
    decorationExists: (from: number, to: number) => {
      const set = extensionStorage.decorationSet.get(editor);

      if (set == null) {
        return false;
      }

      return set.find(from, to).length > 0;
    },
    removeAllDecorations: () => {
      extensionStorage.decorationSet.set(editor, DecorationSet.empty);
      extensionStorage.rangeSet.set(editor, []);
    },
    getRangeSet,
    addRange,
    removeRange,
  };
};

export const addAskAiSelectionHighlight = (
  editor: Editor,
  tr: Transaction,
  from: number,
  to: number,
  attributes?: AskAiSelectionHighlightAttributes,
) => {
  const { addDecoration } = decorationStorageManager(editor, tr);

  addDecoration(from, to, attributes);
};
export const addAskAiSelectionHighlightCommand =
  (from: number, to: number, attributes?: AskAiSelectionHighlightAttributes) =>
  ({ editor, tr }: CommandProps) => {
    addAskAiSelectionHighlight(editor, tr, from, to, attributes);

    return true;
  };
export const removeAskAiSelectionHighlight = (
  editor: Editor,
  tr: Transaction,
  from: number,
  to: number,
) => {
  const { removeDecoration } = decorationStorageManager(editor, tr);

  removeDecoration(from, to);
};
export const removeAskAiSelectionHighlightCommand =
  (from: number, to: number) =>
  ({ editor, tr }: CommandProps) => {
    removeAskAiSelectionHighlight(editor, tr, from, to);

    return true;
  };
export const removeAllAskAiSelectionHighlights = (
  editor: Editor,
  tr: Transaction,
) => {
  const { removeAllDecorations } = decorationStorageManager(editor, tr);

  removeAllDecorations();
};
export const removeAllAskAiSelectionHighlightsCommand =
  () =>
  ({ editor, tr }: CommandProps) => {
    removeAllAskAiSelectionHighlights(editor, tr);

    return true;
  };
